import { useCallback } from 'react';
import {
  DropzoneOptions,
  ErrorCode,
  FileError,
  FileRejection,
} from 'react-dropzone';
import { type DocumentDto } from '@sib/api';
import {
  DOCUMENTS_DROPZONE_ACCEPTED_FILES,
  TICKETS_ATTACHMENTS_DROPZONE_ACCEPTED_FILES,
  TICKET_ATTACHMENTS_DROPZONE_MAX_SIZE_BYTES,
  DOCUMENTS_DROPZONE_MAX_SIZE_BYTES,
  DOCUMENTS_MAX_SIZE_GB,
  DOCUMENT_EMPTY_DATE_FORMAT,
} from '@sib/config';
import { useTranslation } from '@sib/i18n';
import { FileItem, FileStatus } from '@sib/types';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { DOCUMENTS_DATE_FORMAT } from './files.constants';

export const DOCUMENTS_DROPZONE_CONFIG: Partial<DropzoneOptions> = {
  accept: DOCUMENTS_DROPZONE_ACCEPTED_FILES,
  maxSize: DOCUMENTS_DROPZONE_MAX_SIZE_BYTES,
};

export const TICKET_DROPZONE_CONFIG: Partial<DropzoneOptions> = {
  accept: TICKETS_ATTACHMENTS_DROPZONE_ACCEPTED_FILES,
  maxSize: TICKET_ATTACHMENTS_DROPZONE_MAX_SIZE_BYTES,
};
export const getFileCounts = (files: FileItem[]) => {
  const validFilesCount = files.filter(
    ({ status }) => status === FileStatus.Pending
  ).length;

  return `${validFilesCount}/${files.length}`;
};

export type BatchUploadOutcome =
  | { kind: 'mixed'; successCount: number; errorCount: number }
  | { kind: 'fullSuccess' }
  | { kind: 'failuresOnly'; failedFiles: FileItem[] }
  | { kind: 'noCompletedUploads' };

export const classifyBatchUploadOutcome = (
  files: FileItem[]
): BatchUploadOutcome => {
  const uploadedCount = files.filter(
    f => f.status === FileStatus.Uploaded
  ).length;
  const failedFiles = files.filter(f => f.status === FileStatus.Error);
  const failedCount = failedFiles.length;

  if (uploadedCount > 0 && failedCount > 0)
    return {
      kind: 'mixed',
      successCount: uploadedCount,
      errorCount: failedCount,
    };

  if (uploadedCount > 0) return { kind: 'fullSuccess' };
  if (failedCount > 0) return { kind: 'failuresOnly', failedFiles };

  return { kind: 'noCompletedUploads' };
};

export const splitDocumentFilename = (documentName?: string) => {
  const fileName = documentName || '';
  const extensionDot = fileName.lastIndexOf('.');

  const extension = extensionDot !== -1 ? fileName.slice(extensionDot) : '';
  const nameWithoutExtension =
    extensionDot !== -1 ? fileName.slice(0, extensionDot) : fileName;

  const getDocumentNameWithExtension = (value: string) =>
    `${value}${extension}`;

  return { nameWithoutExtension, extension, getDocumentNameWithExtension };
};

export const formatDocumentUploadDate = (doc?: DocumentDto | null): string => {
  if (!doc?.createdAt) return DOCUMENT_EMPTY_DATE_FORMAT;

  const date = new Date(doc.createdAt);
  if (Number.isNaN(date.getTime())) return DOCUMENT_EMPTY_DATE_FORMAT;

  return format(date, DOCUMENTS_DATE_FORMAT);
};

const formatNumberPart = (value: number) => String(value).padStart(2, '0');

const normalizeBaseName = (baseName: string) =>
  baseName.trim().replace(/\s+/g, '-');

export const generateExportFileName = (baseName: string, date = new Date()) => {
  const datePart = `${date.getFullYear()}-${formatNumberPart(
    date.getMonth() + 1
  )}-${formatNumberPart(date.getDate())}`;
  const timePart = `${formatNumberPart(date.getHours())}-${formatNumberPart(
    date.getMinutes()
  )}-${formatNumberPart(date.getSeconds())}`;

  return `${normalizeBaseName(baseName)}-${datePart}-${timePart}`;
};

interface UseOnDropHandlerProps {
  addFiles: (files: FileItem[]) => void;
  maxSize?: string;
}

export const useOnDropHandler = ({
  addFiles,
  maxSize = `${DOCUMENTS_MAX_SIZE_GB} GB`,
}: UseOnDropHandlerProps) => {
  const { t } = useTranslation();

  const getErrorMessage = useCallback(
    (errors: readonly FileError[]) => {
      if (errors.some(err => err.code === ErrorCode.FileTooLarge))
        return t('fileManager.sizeExceedsLimit', {
          limit: maxSize,
        });

      return t('fileManager.invalidFormat');
    },
    [t, maxSize]
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const newAcceptedFiles = acceptedFiles.map(file => ({
        id: uuidv4(),
        file,
        selected: false,
        status: FileStatus.Pending,
        progress: 0,
      }));

      const newRejectedFiles = fileRejections.map(({ file, errors }) => ({
        id: uuidv4(),
        file,
        selected: false,
        status: FileStatus.Error,
        errorMessage: getErrorMessage(errors),
        progress: 0,
      }));

      addFiles([...newAcceptedFiles, ...newRejectedFiles]);
    },
    [addFiles, getErrorMessage]
  );

  return { onDrop };
};

export const downloadFileFromUrl = (url: string, fileName?: string) => {
  const link = document.createElement('a');
  link.href = url;
  if (fileName) {
    link.download = fileName;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadPublicFile = (fileName: string) => {
  const url = `${window.location.origin}/${fileName}`;

  downloadFileFromUrl(url, fileName);
};

export const downloadBlobAsFile = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);

  downloadFileFromUrl(url, fileName);

  URL.revokeObjectURL(url);
};
